package com.hyperether.goodjob.scenes.map

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.text.selection.SelectionContainer
import androidx.compose.material3.DrawerState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.derivedStateOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.navigation.NavHostController
import com.hyperether.goodjob.Dp
import com.hyperether.goodjob.log
import com.hyperether.goodjob.map.MapComponent
import com.hyperether.goodjob.repository.prefs.PrefsManager
import com.hyperether.goodjob.repository.remote.model.LatLng
import com.hyperether.goodjob.resources.Res
import com.hyperether.goodjob.resources.no_information
import com.hyperether.goodjob.resources.places
import com.hyperether.goodjob.scenes.components.MobileHeaderSearchAction
import com.hyperether.goodjob.scenes.components.SearchField
import com.hyperether.goodjob.scenes.dashboard.PlacesJobCard
import com.hyperether.goodjob.scenes.jobs.components.FilterJobsDialog
import com.hyperether.goodjob.scenes.register.FullScreenProgressBar
import com.hyperether.goodjob.util.DateTimeUtil
import com.hyperether.goodjob.util.FilterDialogStateManager
import com.hyperether.goodjob.util.JobStatusMap
import com.hyperether.resources.stringResource
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch

@Composable
fun MapScreen(
    viewModel: MapViewModel,
    navHostController: NavHostController,
    drawerState: DrawerState,
    prefsManager: PrefsManager,
    isMobile: Boolean
) {

    log.d("MAP") { "MapScreen: init" }
    val scope = rememberCoroutineScope()
    val showLoader = remember { mutableStateOf(false) }
    val isInSearchMode = remember { mutableStateOf(false) }
    val jobSearchText = remember { mutableStateOf("") }
    val filteredJobsSearch by remember { derivedStateOf { viewModel.searchMapJobs(jobSearchText.value) } }
    var drawerIsOpened by remember { mutableStateOf(false) }
    val drawerIsClosed by remember { derivedStateOf { drawerState.isAnimationRunning || drawerState.isOpen } }
    LaunchedEffect(Unit) {
        FilterDialogStateManager.setDialogShowing(false)
        if (isMobile) {
            viewModel.getUserPosition()
            viewModel.loadRemoteData()
        }
    }
    viewModel.jobStatusMap = JobStatusMap()

    if (!isMobile) {
        SelectionContainer {
            MapScreenWeb(viewModel, prefsManager, navHostController)
        }
    } else {
        Column {
            MobileHeaderSearchAction(
                title = stringResource(Res.string.places),
                isActionVisible = viewModel.jobList.isNotEmpty(),
                action = {
                    FilterDialogStateManager.setDialogShowing(true)
                },
                search = { isInSearchMode.value = true },
                onOpenDrawer = {
                    scope.launch {
                        drawerIsOpened = true
                        drawerState.open()
                    }
                },
                scope = scope,
                user = prefsManager.getUser(),
                onCancelSearch = {
                    jobSearchText.value = ""
                    isInSearchMode.value = false
                },
            )

            //todo should we hide everything if there is no jobs?

            Column(
                modifier = Modifier.weight(1f)
            ) {
                if (isInSearchMode.value) {
                    Spacer(modifier = Modifier.height(Dp(10f)))
                    SearchField(jobSearchText)
                    Spacer(modifier = Modifier.height(Dp(10f)))
                }

                // todo this key is here to force recreation of ios map view.
                // todo tried to pass currentPosition.value in MapComponent but it doesn't work for ios
                key(viewModel.markers.size, viewModel.selectedMarkerPosition.value) {
                    LaunchedEffect(Unit) {
                        viewModel.getUserPosition()
                    }
                    Column(
                        modifier = Modifier.fillMaxHeight()
                    ) {
                        viewModel.markers.forEach {
                            log.d("MAP") { "Marker for map: ${it.title}" }
                        }
                        MapComponent(
                            showMapWeb = mutableStateOf(true),
                            showMap = !FilterDialogStateManager.dialogShowing.value && (!drawerIsOpened || !drawerIsClosed),
                            markers = viewModel.markers,
                            currentPosition = viewModel.centerPosition,
                            onMapPanned = { lat, lon ->
                                viewModel.centerPosition.value = LatLng(lat, lon)
                            }
                        )
                    }
                }
            }

            val sortedJobList = viewModel.getSortedJobList(filteredJobsSearch)
            if (sortedJobList.isNotEmpty()) {
                if (viewModel.selectedJob.value == null) {
                    viewModel.selectedJob.value = sortedJobList.firstOrNull()
                }

                Column(
                    modifier = Modifier
                        .height(Dp(120f))
                        .fillMaxWidth()
                        .background(Color.White)
                        .padding(start = Dp(16f), top = Dp(5f), bottom = Dp(5f))
                ) {
                    LazyRow(modifier = Modifier.fillMaxWidth()) {
                        items(items = sortedJobList) { job ->
                            PlacesJobCard(
                                job.jobTitle ?: stringResource(Res.string.no_information),
                                job.status ?: stringResource(Res.string.no_information),
                                if (job.startDate?.isNotEmpty() == true) {
                                    job.startDate?.let {
                                        DateTimeUtil.formatJobDetailsDate(
                                            it
                                        )
                                    }
                                        ?: stringResource(Res.string.no_information)
                                } else stringResource(Res.string.no_information),
                                viewModel.sitesMap[job.contactSiteId]?.siteName
                                    ?: stringResource(Res.string.no_information),
                                job.assignees?.employees?.firstOrNull()?.getName(),
                                onClick = {
                                    scope.launch {
                                        showLoader.value = true
                                        viewModel.sitesMap[job.contactSiteId]?.addressObject?.let { addressObject ->
                                            addressObject.lat?.let { lat ->
                                                addressObject.long?.let { lon ->
                                                    viewModel.selectedJob.value = job
                                                    viewModel.selectedMarkerPosition.value =
                                                        LatLng(lat, lon)
                                                    viewModel.centerPosition.value =
                                                        LatLng(lat, lon)
                                                }
                                            }
                                        }
                                        delay(200)
                                        showLoader.value = false
                                    }
                                }
                            )
                        }
                    }
                }
            }
        }

        if (showLoader.value) {
            FullScreenProgressBar()
        }

        val userTeams = viewModel.getUser()?.teams
        if (FilterDialogStateManager.dialogShowing.value) {
            FilterJobsDialog(
                filterMap = viewModel.getFilterMap(),
                onDismiss = { FilterDialogStateManager.setDialogShowing(false) },
                onApply = { map ->
                    FilterDialogStateManager.setDialogShowing(false)
                    viewModel.applyFilter(map)
                    viewModel.filterJobs()
                },
                isMobileView = isMobile,
                isEmployee = viewModel.isEmployee(),
                userTeams = userTeams
            )
        }
    }
}